import { ZodError, z } from "zod";

export type ErrorType = "unauthorized_error" | "request_error" | "unknown_error";

export type FetchError = UnknownError | UnauthorizedError | RequestError | JsonParseError;

export class WheeError extends Error {
  readonly message: string;
  readonly type: string;
  userFacingMessage?: string;
  statusCode?: number;

  constructor(message: string) {
    super(message);

    this.type = this.constructor.name;
    this.message = message;
  }

  toResponse(): Response {
    return new Response(
      JSON.stringify({
        error_type: this.type,
        message:
          this.userFacingMessage ??
          "En uventet feil skjedde. Feilen er mest sannsynlig hos oss.\nKontakt oss på hei@whee.no hvis feilen vedvarer."
      }),
      {
        status: this.statusCode ?? 500
      }
    );
  }

  static generateFromUnknown(error: unknown): WheeError {
    if (error instanceof WheeError) {
      return error;
    }

    if (error instanceof Error) {
      return new UnknownError(error);
    }

    return new UnknownError();
  }
}

export class UnauthorizedError extends WheeError {
  toResponse(message?: string): Response {
    return new Response(
      JSON.stringify({
        error_type: this.constructor.name,
        message:
          message ??
          "Du mangler riktig tilgang for å gjøre denne handlingen.\nKontakt oss på hei@whee.no hvis du mener dette er feil."
      }),
      {
        status: 401
      }
    );
  }
}

export class RequestError extends WheeError {
  readonly statusCode: number;
  readonly body?: string;
  constructor(message: string, statusCode: number, body?: string) {
    super(message);
    this.statusCode = statusCode;
    this.body = body;
  }
}

export class AirtableRequestError extends WheeError {
  readonly statusCode: number;
  readonly body?: string;
  readonly table?: string;
  constructor(message: string, statusCode: number, table?: string, body?: string) {
    super(message);
    this.table = table;
    this.statusCode = statusCode;
    this.body = body;
  }
}

export class UnknownError extends WheeError {
  constructor(error?: Error) {
    if (error) {
      super(error.message);
    } else {
      super("An unknown error has occurred");
    }
  }
}

export class JsonParseError extends WheeError {
  readonly path?: (string | number)[];
  readonly body?: unknown;

  constructor(message: string, path?: (string | number)[], body?: unknown) {
    super(message);
    this.path = path;
    this.body = body;
  }

  static generateFromUnknown(error: unknown, body?: unknown): JsonParseError {
    if (error instanceof ZodError) {
      return new JsonParseError(
        error.errors.map((err) => err.message).join("\n"),
        error.errors.flatMap((err) => err.path),
        body
      );
    }
    if (error instanceof Error) {
      return new JsonParseError(error.message, undefined, body);
    }

    return new JsonParseError("Unknown error occurred when parsing JSON object", undefined, body);
  }
}

// Utility function really
export const errorMessageFromZod = (zodError: z.ZodError<any>) => {
  const message = Object.entries(zodError.formErrors.fieldErrors).map(([field, fieldMsg]) => {
    if (!fieldMsg) {
      return "";
    }
    return `Error in key ${field}: ${fieldMsg.join(" and ").toLocaleLowerCase()}`;
  });
  return new Response(`Invalid JSON body.\n\n${message.join("\n")}`, { status: 400 });
};
